import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Chip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel,
  Grid, Card, CardContent, Divider, IconButton, Tooltip, Stack, Checkbox,
} from '@mui/material';
import {
  Add, Delete, PlayArrow, Pause, Send, TouchApp, OpenInNew, Instagram,
  Campaign as CampaignIcon, PhotoLibrary, Close, CheckCircle, Link as LinkIcon,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../components/AuthProvider';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';

const WORKERS_URL = 'https://share2dm-webhook.share2dm.workers.dev';

type PlanName = 'free' | 'standard' | 'growth' | 'pro';
const PLAN_LIMITS: Record<PlanName, { dmPerMonth: number; maxCampaigns: number }> = {
  free:     { dmPerMonth: 5_000,   maxCampaigns: 1 },
  standard: { dmPerMonth: 50_000,  maxCampaigns: 5 },
  growth:   { dmPerMonth: 200_000, maxCampaigns: 15 },
  pro:      { dmPerMonth: -1,      maxCampaigns: -1 },
};

interface Campaign {
  id: string;
  reel_url: string;
  ig_contents_id: string;
  response_message: string;
  product_url: string;
  is_active: boolean;
  created_at: string;
  campaign_type: 'reel_share' | 'comment_automation';
  trigger_keywords: string[];
  comment_reply_message: string | null;
}

interface StickerClickStat {
  product_id: string;
  product_name: string | null;
  external_url: string | null;
  start_time: number | null;
  count: number;
}

interface CampaignStats {
  dmsSent: number;
  clicks: number;
  stickerClicks: number;
  stickerBreakdown: StickerClickStat[];
  queuePending: number;
  queueSending: number;
  commentCount: number;
}

interface Brand {
  id: string;
  brand_name: string;
  ig_username: string | null;
  ig_account_id: string | null;
  plan: PlanName;
  notification_email: string | null;
  creator_pool_agreed: boolean | null;
}

interface ClozetContent {
  found: boolean;
  content_id?: string;
  short_code?: string;
  clozet_url?: string;
  title?: string;
}

interface MediaItem {
  id: string;
  shortcode: string;
  media_type: string;
  thumbnail_url?: string;
  media_url?: string;
  timestamp: string;
  permalink: string;
}

export default function Share2dmCampaigns() {
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);

  const [brand, setBrand] = useState<Brand | null>(null);
  const [brandLoading, setBrandLoading] = useState(true);
  const [brandError, setBrandError] = useState<string | null>(null);

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignStats, setCampaignStats] = useState<Record<string, CampaignStats>>({});
  const [dmUsed, setDmUsed] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [campaignType, setCampaignType] = useState<'reel_share' | 'comment_automation'>('reel_share');

  // Creator Pool 동의 모달
  const [showCreatorPoolModal, setShowCreatorPoolModal] = useState(false);
  const [creatorPoolAgreed, setCreatorPoolAgreed] = useState(false);
  const [creatorPoolAgreeing, setCreatorPoolAgreeing] = useState(false);

  // 미디어 피커
  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [mediaListLoading, setMediaListLoading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const loadMediaList = async () => {
    if (!brand) return;
    setShowMediaPicker(true);
    if (mediaList.length > 0) return;
    setMediaListLoading(true);
    try {
      const res = await fetch(`${WORKERS_URL}/media-list?brand_id=${brand.id}`);
      const data: any = await res.json();
      if (data.media) setMediaList(data.media);
    } catch { /* ignore */ } finally {
      setMediaListLoading(false);
    }
  };

  const selectMedia = (item: MediaItem) => {
    handleReelUrlChange(item.permalink);
    setShowMediaPicker(false);
  };
  const [form, setForm] = useState({
    reel_url: '',
    response_message: '',
    product_url: '',
    trigger_keywords: '',
    comment_reply_message: '',
  });
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [clozetContent, setClozetContent] = useState<ClozetContent | null>(null);
  const [clozetConnecting, setClozetConnecting] = useState(false);
  const [productUrlSource, setProductUrlSource] = useState<'manual' | 'clozet'>('manual');

  // 1. 브랜드 조회 (clozet_seller_id → seller_profile.id)
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setBrandLoading(true);
      setBrandError(null);
      try {
        // seller_profile.id 조회
        const { data: sp } = await supabase
          .from('seller_profile')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (!sp) {
          setBrandError('seller_profile을 찾을 수 없습니다.');
          return;
        }

        // share2dm_brands에서 clozet_seller_id로 조회
        const { data: b } = await supabase
          .from('share2dm_brands')
          .select('id, brand_name, ig_username, ig_account_id, plan, notification_email, creator_pool_agreed')
          .eq('clozet_seller_id', sp.id)
          .single();

        if (b) {
          setBrand(b as Brand);

          // Creator인데 아직 Pool 동의 안 한 경우 모달 표시
          const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id)
            .single();
          if (profile?.role === 'creator' && !b.creator_pool_agreed) {
            setShowCreatorPoolModal(true);
          }
        } else {
          // 연동 안 된 상태
          setBrandError('instagram_not_linked');
        }
      } catch (e: any) {
        setBrandError(e.message);
      } finally {
        setBrandLoading(false);
      }
    };
    load();
  }, [user]);

  // Creator Pool 동의 처리
  const handleCreatorPoolAgree = async () => {
    if (!brand || !creatorPoolAgreed) return;
    setCreatorPoolAgreeing(true);
    await supabase
      .from('share2dm_brands')
      .update({ creator_pool_agreed: true, creator_pool_agreed_at: new Date().toISOString() })
      .eq('id', brand.id);
    setBrand(b => b ? { ...b, creator_pool_agreed: true } : b);
    setShowCreatorPoolModal(false);
    setCreatorPoolAgreeing(false);
  };

  // 2. 캠페인 + 사용량 조회
  const loadCampaigns = useCallback(async () => {
    if (!brand) return;
    const { data } = await supabase
      .from('share2dm_campaigns')
      .select('*')
      .eq('brand_id', brand.id)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });
    if (data) setCampaigns(data as Campaign[]);
  }, [brand]);

  const loadDmUsage = useCallback(async () => {
    if (!brand) return;
    const startOfMonth = new Date();
    startOfMonth.setUTCDate(1);
    startOfMonth.setUTCHours(0, 0, 0, 0);
    const { count } = await supabase
      .from('share2dm_dm_logs')
      .select('*', { count: 'exact', head: true })
      .eq('brand_id', brand.id)
      .gte('dm_sent_at', startOfMonth.toISOString());
    setDmUsed(count ?? 0);
  }, [brand]);

  useEffect(() => {
    if (brand) { loadCampaigns(); loadDmUsage(); }
  }, [brand, loadCampaigns, loadDmUsage]);

  // 3. 캠페인 통계
  const loadStats = useCallback(async () => {
    if (!campaigns.length || !brand) return;
    const stats: Record<string, CampaignStats> = {};
    for (const c of campaigns) {
      const { count: dmCount } = await supabase
        .from('share2dm_dm_logs').select('*', { count: 'exact', head: true }).eq('campaign_id', c.id);
      const { count: clickCount } = await supabase
        .from('share2dm_dm_logs').select('*', { count: 'exact', head: true })
        .eq('campaign_id', c.id).not('link_clicked_at', 'is', null);
      const { data: stickerRows } = await supabase
        .from('share2dm_sticker_clicks')
        .select('product_id, product_name, external_url, start_time')
        .eq('campaign_id', c.id);
      const stickerClickCount = stickerRows?.length ?? 0;
      // 스티커별 집계 (product_id + start_time 조합으로 구분)
      const stickerMap: Record<string, StickerClickStat> = {};
      for (const row of stickerRows ?? []) {
        const key = `${row.product_id}_${row.start_time ?? 'null'}`;
        if (!stickerMap[key]) {
          stickerMap[key] = { product_id: row.product_id, product_name: row.product_name, external_url: row.external_url, start_time: row.start_time, count: 0 };
        }
        stickerMap[key].count++;
      }
      // 영상 시간 순서로 정렬
      const stickerBreakdown = Object.values(stickerMap).sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0));
      let commentCount = 0;
      if (c.campaign_type === 'comment_automation') {
        const { count } = await supabase
          .from('share2dm_comment_logs').select('*', { count: 'exact', head: true }).eq('campaign_id', c.id);
        commentCount = count ?? 0;
      }
      let queuePending = 0, queueSending = 0;
      try {
        const res = await fetch(`${WORKERS_URL}/queue/status?brand_id=${brand.id}&campaign_id=${c.id}`);
        const q: any = await res.json();
        queuePending = q.queue?.pending ?? 0;
        queueSending = q.queue?.sending ?? 0;
      } catch { /* ignore */ }
      stats[c.id] = { dmsSent: dmCount ?? 0, clicks: clickCount ?? 0, stickerClicks: stickerClickCount, stickerBreakdown, queuePending, queueSending, commentCount };
    }
    setCampaignStats(stats);
  }, [campaigns, brand]);

  useEffect(() => { loadStats(); }, [campaigns, loadStats]);

  // 캠페인 생성
  const extractShortCode = (link: string) => {
    const m = link.match(/\/reel\/([A-Za-z0-9_-]+)/) || link.match(/\/p\/([A-Za-z0-9_-]+)/);
    return m ? m[1] : '';
  };

  // Clozet Connect: reel URL → Clozet 컨텐츠 자동 매칭
  const checkClozetConnect = async (reelUrl: string) => {
    if (!brand) return;
    const shortCode = extractShortCode(reelUrl);
    if (!shortCode) { setClozetContent(null); return; }
    setClozetConnecting(true);
    try {
      const res = await fetch(`${WORKERS_URL}/clozet/contents?brand_id=${brand.id}&ig_code=${shortCode}`);
      const data: ClozetContent = await res.json();
      setClozetContent(data);
      if (data.found && data.clozet_url) {
        setForm(p => ({ ...p, product_url: data.clozet_url! }));
        setProductUrlSource('clozet');
      }
    } catch { setClozetContent(null); } finally {
      setClozetConnecting(false);
    }
  };

  const handleReelUrlChange = (url: string) => {
    setForm(p => ({ ...p, reel_url: url }));
    setClozetContent(null);
    setProductUrlSource('manual');
    if (url.includes('instagram.com/')) {
      checkClozetConnect(url);
    }
  };

  const handleCreate = async () => {
    if (!brand) return;
    setFormError(null);
    setCreating(true);
    try {
      const limits = PLAN_LIMITS[brand.plan];
      if (limits.maxCampaigns !== -1 && campaigns.length >= limits.maxCampaigns) {
        setFormError(`현재 플랜(${brand.plan})에서는 캠페인을 ${limits.maxCampaigns}개까지 만들 수 있습니다.`);
        return;
      }

      const shortCode = extractShortCode(form.reel_url);
      if (!shortCode) { setFormError('올바른 릴스 또는 포스트 URL을 입력해주세요.'); return; }

      const mediaRes = await fetch(`${WORKERS_URL}/media-id?brand_id=${brand.id}&url=${encodeURIComponent(form.reel_url)}`);
      const mediaData: any = await mediaRes.json();
      if (!mediaData.media_id) {
        setFormError(`미디어 ID 조회 실패: ${mediaData.error ?? '알 수 없는 오류'}`);
        return;
      }

      if (campaignType === 'comment_automation') {
        const keywords = form.trigger_keywords.split(',').map(k => k.trim()).filter(Boolean);
        if (!keywords.length) { setFormError('트리거 키워드를 최소 1개 이상 입력해주세요.'); return; }
        const { error } = await supabase.from('share2dm_campaigns').insert({
          brand_id: brand.id, campaign_type: 'comment_automation',
          reel_url: form.reel_url, ig_contents_id: mediaData.media_id,
          short_code: shortCode, response_message: form.response_message,
          product_url: form.product_url, product_url_source: productUrlSource,
          trigger_keywords: keywords, comment_reply_message: form.comment_reply_message || null,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('share2dm_campaigns').insert({
          brand_id: brand.id, campaign_type: 'reel_share',
          reel_url: form.reel_url, ig_contents_id: mediaData.media_id,
          short_code: shortCode, response_message: form.response_message,
          product_url: form.product_url, product_url_source: productUrlSource,
        });
        if (error) throw error;
      }

      setForm({ reel_url: '', response_message: '', product_url: '', trigger_keywords: '', comment_reply_message: '' });
      setClozetContent(null);
      setProductUrlSource('manual');
      setShowForm(false);
      loadCampaigns();
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const toggleCampaign = async (id: string, isActive: boolean) => {
    await supabase.from('share2dm_campaigns').update({ is_active: !isActive }).eq('id', id);
    loadCampaigns();
  };

  const deleteCampaign = async (id: string) => {
    if (!window.confirm('캠페인을 삭제하시겠습니까?')) return;
    await supabase.from('share2dm_campaigns').update({ deleted_at: new Date().toISOString() }).eq('id', id);
    loadCampaigns();
  };

  // Instagram 연동 재시도
  const handleRelink = async () => {
    if (!user) return;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.provider_token) {
      alert('세션이 만료되었습니다. 로그아웃 후 다시 로그인해주세요.');
      return;
    }
    const { data: sp } = await supabase.from('seller_profile').select('id, contact_email').eq('user_id', user.id).single();
    if (!sp) return;

    const res = await fetch(`${WORKERS_URL}/auth/init-from-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: session.provider_token, clozet_seller_id: sp.id, notification_email: sp.contact_email }),
    });
    const data: any = await res.json();
    if (data.error) { alert(`연동 실패: ${data.error}`); return; }
    window.location.reload();
  };

  // --- 렌더링 ---
  if (brandLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (brandError === 'instagram_not_linked') {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} mb={3} color={isDarkMode ? '#fff' : '#111'}>
          DM 캠페인
        </Typography>
        <Paper elevation={0} sx={{ ...styles.paper, p: 5, textAlign: 'center', borderRadius: 3 }}>
          <Instagram sx={{ fontSize: 56, color: '#E1306C', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} mb={1}>Instagram 계정 연동이 필요합니다</Typography>
          <Typography color="text.secondary" mb={3} fontSize={14}>
            DM 자동화를 사용하려면 Instagram 비즈니스 계정을 연동해야 합니다.
          </Typography>
          <Button variant="contained" onClick={handleRelink}
            sx={{ bgcolor: '#E1306C', '&:hover': { bgcolor: '#be185d' }, fontWeight: 700, px: 4, py: 1.2 }}>
            Instagram 연동하기
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" mt={2}>
            로그인 시 사용한 Facebook 계정으로 자동 연동됩니다.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (brandError) {
    return <Alert severity="error">{brandError}</Alert>;
  }

  const planLimits = PLAN_LIMITS[brand?.plan ?? 'free'];
  const dmLimit = planLimits.dmPerMonth;

  return (
    <Box>
      {/* Creator Pool 동의 모달 */}
      <Dialog open={showCreatorPoolModal} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Clozet 크리에이터 풀 등록 동의</DialogTitle>
        <DialogContent>
          <Typography fontSize={14} color="text.secondary" mb={2}>
            Share2DM을 크리에이터로 사용하시려면 Clozet 크리에이터 풀 등록에 동의가 필요합니다.
          </Typography>
          <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 2, p: 2, mb: 2 }}>
            <Typography fontSize={13} fontWeight={600} mb={1}>동의 내용</Typography>
            <Typography fontSize={13} color="text.secondary" lineHeight={1.8}>
              • 크리에이터 정보(활동명, 카테고리, 팔로워 규모)가 Clozet 브랜드 Affiliate 매칭에 활용됩니다.<br />
              • 향후 브랜드 Affiliate 프로그램 오픈 시 우선권이 부여됩니다.<br />
              • 동의 철회는 고객센터를 통해 요청 가능합니다.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={creatorPoolAgreed}
                onChange={e => setCreatorPoolAgreed(e.target.checked)}
                size="small"
                sx={{ color: '#111', '&.Mui-checked': { color: '#111' } }}
              />
            }
            label={<Typography fontSize={13}>Clozet 크리에이터 풀 등록에 동의합니다.</Typography>}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            fullWidth variant="contained"
            onClick={handleCreatorPoolAgree}
            disabled={!creatorPoolAgreed || creatorPoolAgreeing}
            sx={{ py: 1.2, fontWeight: 700, bgcolor: '#111', '&:hover': { bgcolor: '#333' } }}
          >
            {creatorPoolAgreeing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : '동의하고 시작하기'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 헤더 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color={isDarkMode ? '#fff' : '#111'}>
            DM 캠페인
          </Typography>
          {brand?.ig_username && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Instagram sx={{ fontSize: 16, color: '#E1306C' }} />
              <Typography variant="body2" color="text.secondary">@{brand.ig_username}</Typography>
            </Box>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setShowForm(true)}
          sx={{ bgcolor: '#E1306C', '&:hover': { bgcolor: '#be185d' }, fontWeight: 700 }}
        >
          캠페인 생성
        </Button>
      </Box>

      {/* 플랜 & 사용량 */}
      <Paper elevation={0} sx={{ ...styles.paper, p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            label={brand?.plan?.toUpperCase()}
            size="small"
            sx={{
              fontWeight: 700,
              bgcolor: brand?.plan === 'pro' ? '#7c3aed' : brand?.plan === 'growth' ? '#059669' : brand?.plan === 'standard' ? '#2563eb' : '#6b7280',
              color: '#fff',
            }}
          />
          <Typography variant="body2" color="text.secondary">
            이번 달 DM 발송: <strong>{dmUsed.toLocaleString()}</strong>
            {dmLimit !== -1 ? ` / ${dmLimit.toLocaleString()}` : ' (무제한)'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            활성 캠페인: <strong>{campaigns.length}</strong>
            {planLimits.maxCampaigns !== -1 ? ` / ${planLimits.maxCampaigns}` : ' (무제한)'}
          </Typography>
        </Box>
      </Paper>

      {/* 캠페인 목록 */}
      {campaigns.length === 0 ? (
        <Paper elevation={0} sx={{ ...styles.paper, p: 5, textAlign: 'center', borderRadius: 3 }}>
          <CampaignIcon sx={{ fontSize: 48, color: isDarkMode ? '#555' : '#ddd', mb: 2 }} />
          <Typography variant="h6" fontWeight={600} color="text.secondary">
            아직 캠페인이 없습니다
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1} mb={3}>
            릴스 공유 시 자동으로 DM을 발송하는 캠페인을 만들어보세요.
          </Typography>
          <Button variant="outlined" startIcon={<Add />} onClick={() => setShowForm(true)}>
            첫 캠페인 만들기
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {campaigns.map(c => {
            const stats = campaignStats[c.id];
            const isComment = c.campaign_type === 'comment_automation';
            return (
              <Paper key={c.id} elevation={0} sx={{ ...styles.paper, p: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Chip
                        label={isComment ? '댓글 자동화' : '릴스 공유'}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          bgcolor: isComment ? '#7c3aed20' : '#E1306C20',
                          color: isComment ? '#7c3aed' : '#E1306C',
                          fontWeight: 600,
                        }}
                      />
                      <Chip
                        label={c.is_active ? '활성' : '중지'}
                        size="small"
                        color={c.is_active ? 'success' : 'default'}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {c.reel_url}
                      <IconButton size="small" component="a" href={c.reel_url} target="_blank" sx={{ ml: 0.5 }}>
                        <OpenInNew sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Typography>
                    <Typography variant="body2" mt={1} color={isDarkMode ? '#ddd' : '#333'}>
                      💬 {c.response_message}
                    </Typography>
                    {c.product_url && (
                      <Typography variant="caption" color="text.secondary">
                        🔗 {c.product_url}
                      </Typography>
                    )}
                    {isComment && c.trigger_keywords?.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {c.trigger_keywords.map(k => (
                          <Chip key={k} label={k} size="small" sx={{ fontSize: '0.65rem' }} />
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                    <Tooltip title={c.is_active ? '중지' : '활성화'}>
                      <IconButton size="small" onClick={() => toggleCampaign(c.id, c.is_active)}>
                        {c.is_active ? <Pause /> : <PlayArrow />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="삭제">
                      <IconButton size="small" color="error" onClick={() => deleteCampaign(c.id)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {stats && (
                  <Box sx={{ pt: 2, borderTop: `1px solid ${isDarkMode ? '#404040' : '#f0f0f0'}` }}>
                    {/* 요약 수치 */}
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700} color={isDarkMode ? '#fff' : '#111'}>{stats.dmsSent}</Typography>
                        <Typography variant="caption" color="text.secondary">DM 발송</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700} color={isDarkMode ? '#fff' : '#111'}>{stats.clicks}</Typography>
                        <Typography variant="caption" color="text.secondary">링크 클릭</Typography>
                      </Box>
                      {stats.dmsSent > 0 && (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight={700} color="#10b981">
                            {((stats.clicks / stats.dmsSent) * 100).toFixed(1)}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">클릭률</Typography>
                        </Box>
                      )}
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700} color="#6366f1">{stats.stickerClicks}</Typography>
                        <Typography variant="caption" color="text.secondary">스티커 클릭</Typography>
                      </Box>
                      {stats.clicks > 0 && (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight={700} color="#a855f7">
                            {((stats.stickerClicks / stats.clicks) * 100).toFixed(1)}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">스티커 전환율</Typography>
                        </Box>
                      )}
                      {(stats.queuePending > 0 || stats.queueSending > 0) && (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight={700} color="#f59e0b">
                            {stats.queuePending + stats.queueSending}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">대기 중</Typography>
                        </Box>
                      )}
                      {isComment && (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight={700} color={isDarkMode ? '#fff' : '#111'}>{stats.commentCount}</Typography>
                          <Typography variant="caption" color="text.secondary">댓글 감지</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* 스티커별 클릭 breakdown */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        스티커별 클릭
                      </Typography>
                      {stats.stickerBreakdown.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">아직 클릭 데이터가 없습니다.</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {stats.stickerBreakdown.map((s) => (
                            <Box key={`${s.product_id}_${s.start_time}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {s.start_time != null && (
                                    <Chip
                                      label={`${Math.floor(s.start_time / 60)}:${String(Math.floor(s.start_time % 60)).padStart(2, '0')}`}
                                      size="small"
                                      sx={{ height: 16, fontSize: 10, bgcolor: isDarkMode ? '#333' : '#f0f0f0', color: 'text.secondary' }}
                                    />
                                  )}
                                  <Typography variant="caption" noWrap color={isDarkMode ? '#ddd' : '#333'}>
                                    {s.product_name || s.product_id}
                                  </Typography>
                                </Box>
                                {s.external_url && (
                                  <Typography variant="caption" noWrap color="text.secondary" display="block" sx={{ fontSize: 10 }}>
                                    {s.external_url}
                                  </Typography>
                                )}
                              </Box>
                              <Chip
                                label={`${s.count}회`}
                                size="small"
                                sx={{ bgcolor: '#6366f120', color: '#6366f1', fontWeight: 700, fontSize: 11, height: 20 }}
                              />
                              {stats.clicks > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: 'right' }}>
                                  {((s.count / stats.clicks) * 100).toFixed(0)}%
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* 캠페인 생성 다이얼로그 */}
      <Dialog open={showForm} onClose={() => { setShowForm(false); setFormError(null); setShowMediaPicker(false); setClozetContent(null); setProductUrlSource('manual'); }} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>새 캠페인 만들기</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}

            <FormControl fullWidth size="small">
              <InputLabel>캠페인 유형</InputLabel>
              <Select
                value={campaignType}
                label="캠페인 유형"
                onChange={e => setCampaignType(e.target.value as any)}
              >
                <MenuItem value="reel_share">릴스 공유 DM — 공유 시 자동 DM 발송</MenuItem>
                <MenuItem value="comment_automation">댓글 자동화 — 키워드 댓글 감지 후 DM</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth size="small"
                  label="릴스 / 포스트 URL"
                  placeholder="https://www.instagram.com/reel/..."
                  value={form.reel_url}
                  onChange={e => handleReelUrlChange(e.target.value)}
                  InputProps={{
                    endAdornment: clozetConnecting ? <CircularProgress size={16} sx={{ mr: 1 }} /> : undefined,
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PhotoLibrary />}
                  onClick={loadMediaList}
                  sx={{ whiteSpace: 'nowrap', minWidth: 120 }}
                >
                  목록에서 선택
                </Button>
              </Box>

              {/* 미디어 피커 */}
              {showMediaPicker && (
                <Paper elevation={2} sx={{ mt: 1, borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, bgcolor: isDarkMode ? '#333' : '#f9f9f9', borderBottom: '1px solid #eee' }}>
                    <Typography variant="body2" fontWeight={600}>내 Instagram 게시물</Typography>
                    <IconButton size="small" onClick={() => setShowMediaPicker(false)}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                  {mediaListLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : mediaList.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      게시물이 없습니다.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '2px', p: '2px', maxHeight: 280, overflowY: 'auto' }}>
                      {mediaList.map(item => {
                        const thumb = item.thumbnail_url || item.media_url;
                        const date = new Date(item.timestamp).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
                        return (
                          <Box
                            key={item.id}
                            onClick={() => selectMedia(item)}
                            sx={{
                              position: 'relative', aspectRatio: '1', overflow: 'hidden',
                              cursor: 'pointer', bgcolor: '#f0f0f0',
                              '&:hover': { opacity: 0.8 },
                            }}
                            title={`${item.shortcode} · ${date}`}
                          >
                            {thumb ? (
                              <img src={thumb} alt={item.shortcode} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                                {item.media_type === 'REEL' ? '🎬' : '🖼️'}
                              </Box>
                            )}
                            <Box sx={{
                              position: 'absolute', bottom: 0, left: 0, right: 0,
                              bgcolor: 'rgba(0,0,0,0.5)', color: '#fff',
                              fontSize: 10, px: 0.5, py: 0.3, textAlign: 'center',
                            }}>
                              {date}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Paper>
              )}
            </Box>

            <TextField
              fullWidth size="small" multiline rows={3}
              label="DM 메시지"
              placeholder="안녕하세요! 공유해주셔서 감사합니다. 상품 링크를 보내드립니다."
              value={form.response_message}
              onChange={e => setForm(p => ({ ...p, response_message: e.target.value }))}
            />

            {/* Clozet Connect 상태 표시 */}
            {clozetContent?.found && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: '#10b98120', borderRadius: 2 }}>
                <CheckCircle sx={{ fontSize: 18, color: '#10b981' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="#10b981">
                    Clozet 컨텐츠 연동됨
                  </Typography>
                  {clozetContent.title && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {clozetContent.title}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" component="a" href={clozetContent.clozet_url} target="_blank">
                  <LinkIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )}
            {clozetContent !== null && !clozetContent.found && form.reel_url && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                Clozet 컨텐츠 없음 — 상품 URL을 직접 입력해주세요.
              </Typography>
            )}

            <TextField
              fullWidth size="small"
              label="상품/링크 URL"
              placeholder="https://..."
              value={form.product_url}
              onChange={e => { setForm(p => ({ ...p, product_url: e.target.value })); setProductUrlSource('manual'); }}
              InputProps={{
                startAdornment: productUrlSource === 'clozet' ? (
                  <Chip label="Clozet" size="small" sx={{ mr: 1, height: 20, fontSize: 10, bgcolor: '#10b98120', color: '#10b981', fontWeight: 700 }} />
                ) : undefined,
              }}
            />

            {campaignType === 'comment_automation' && (
              <>
                <TextField
                  fullWidth size="small"
                  label="트리거 키워드 (쉼표로 구분)"
                  placeholder="링크, 구매, 어디서"
                  value={form.trigger_keywords}
                  onChange={e => setForm(p => ({ ...p, trigger_keywords: e.target.value }))}
                  helperText="댓글에 이 키워드가 포함되면 DM이 발송됩니다."
                />
                <TextField
                  fullWidth size="small"
                  label="댓글 답글 (선택)"
                  placeholder="DM 보내드렸습니다! 확인해주세요 😊"
                  value={form.comment_reply_message}
                  onChange={e => setForm(p => ({ ...p, comment_reply_message: e.target.value }))}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setShowForm(false); setFormError(null); setClozetContent(null); setProductUrlSource('manual'); }}>취소</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !form.reel_url || !form.response_message}
            sx={{ bgcolor: '#E1306C', '&:hover': { bgcolor: '#be185d' }, fontWeight: 700 }}
          >
            {creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : '캠페인 생성'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
